import React, { useCallback } from 'react'
import { graphql, useFragment } from 'react-relay'
import { Trans, useLingui } from '@lingui/react/macro'
import { useNavigate } from 'react-router-dom'

import { XStack, YStack, Select, useTheme } from 'tamagui'

import { Tooltip } from '@newComponents/Tooltip'

import {
  Button,
  Paragraph,
  Icon,
  useCreateLinkPressHandler,
} from '@newComponents'

import { useMutation } from '@data/useMutation'

import {
  StyledSelect,
  StyledSelectContent,
  StyledSelectItem,
  StyledSelectItemText,
  StyledSelectViewport,
} from '@newComponents/Form/FormSelect'

import {
  EventScreenSettingsButton_event$key,
  EventVisibility,
} from './__generated__/EventScreenSettingsButton_event.graphql'
import { EventScreenSettingsButtonUpdateMutation } from './__generated__/EventScreenSettingsButtonUpdateMutation.graphql'
import { EventScreenSettingsButtonCopyEventMutation } from './__generated__/EventScreenSettingsButtonCopyEventMutation.graphql'

export const EventScreenSettingsButton = ({
  event,
}: {
  event: EventScreenSettingsButton_event$key
}) => {
  const { t } = useLingui()

  const VISIBILITY_TEXT: Record<EventVisibility, string> = {
    UNLISTED: t`Unlisted`,
    LISTED: t`Listed`,
    '%future added value': t`Unknown`,
  }

  const data = useFragment(
    graphql`
      fragment EventScreenSettingsButton_event on Event {
        id

        prettyUrl
        visibility

        hasVenue
        hasExternalUrl

        description

        viewerCanManage
      }
    `,
    event
  )

  const theme = useTheme()

  const updateEvent = useMutation<EventScreenSettingsButtonUpdateMutation>(
    graphql`
      mutation EventScreenSettingsButtonUpdateMutation(
        $input: UpdateEventByIdInput!
      ) {
        updateEventById(input: $input) {
          event {
            ...EventScreenNewMobile_event
            ...EventScreenNewDesktop_event
          }
        }
      }
    `
  )

  const copyEvent = useMutation<EventScreenSettingsButtonCopyEventMutation>(
    graphql`
      mutation EventScreenSettingsButtonCopyEventMutation($eventId: ID!) {
        copyEvent(eventId: $eventId) {
          event {
            prettyUrl
          }
        }
      }
    `
  )

  const navigate = useNavigate()
  const linkAction = useCallback(
    () => navigate(`/events/${data.prettyUrl}/settings`),
    [data.prettyUrl]
  )

  const { linkPressHandler, anchorRef } = useCreateLinkPressHandler(linkAction)

  if (data.viewerCanManage) {
    return (
      <XStack
        gap='$6'
        alignItems='center'
        $gtXs={{ paddingLeft: '$5' }}
        $gtSm={{
          paddingLeft: 0,
        }}
      >
        {data.visibility === 'UNLISTED' &&
        data.description &&
        (data.hasVenue || data.hasExternalUrl) ? (
          <StyledSelect
            onValueChange={async (value: EventVisibility) => {
              await updateEvent({
                variables: {
                  input: { id: data.id, patch: { visibility: value } },
                },
              })
            }}
          >
            <Select.Trigger
              flexBasis='content'
              padding={0}
              borderWidth={0}
              outlineWidth={0}
            >
              <Button
                paddingVertical='$2'
                paddingHorizontal='$3'
                minHeight='unset'
                height={32}
                borderRadius={16}
                borderWidth={0}
                gap='$1'
                style={{
                  background: theme.backgroundAccentMuted.val,
                  boxShadow: 'none',
                }}
                hoverStyle={{
                  background: theme.backgroundAccent.val,
                }}
              >
                <Button.Text
                  size='$4'
                  color='$primaryColor'
                  textShadowColor='unset'
                >
                  <Trans>Unlisted</Trans>
                </Button.Text>
              </Button>
            </Select.Trigger>

            <StyledSelectContent zIndex={200_000}>
              <StyledSelectViewport>
                <StyledSelectItem index={0} value='LISTED'>
                  <XStack gap='$2' padding='$1'>
                    <StyledSelectItemText><Trans>List Event</Trans></StyledSelectItemText>
                  </XStack>
                </StyledSelectItem>
              </StyledSelectViewport>
            </StyledSelectContent>
          </StyledSelect>
        ) : !data.description || (!data.hasVenue && !data.hasExternalUrl) ? (
          <Tooltip placement='top' delay={100}>
            <Tooltip.Trigger tag='dialog'>
              <Paragraph
                color={
                  data.visibility === 'UNLISTED' ? '$errorColor' : '$colorMuted'
                }
                fontWeight='$6'
              >
                {VISIBILITY_TEXT[data.visibility]}
              </Paragraph>
            </Tooltip.Trigger>

            <Tooltip.Content>
              <Tooltip.Arrow />

              <YStack gap='$2' alignItems='center'>
                {!data.description && (
                  <Paragraph
                    size='$4'
                    color='$colorOnPrimary'
                    fontWeight='$5'
                    textAlign='center'
                  >
                    <Trans>Missing Description</Trans>
                  </Paragraph>
                )}

                {!data.hasVenue && !data.hasExternalUrl && (
                  <Paragraph
                    size='$4'
                    color='$colorOnPrimary'
                    fontWeight='$5'
                    textAlign='center'
                  >
                    <Trans>Missing Venue or Online URL</Trans>
                  </Paragraph>
                )}
              </YStack>
            </Tooltip.Content>
          </Tooltip>
        ) : (
          <Paragraph
            color={data.visibility === 'UNLISTED' ? '$errorColor' : '$colorMuted'}
            fontWeight='$6'
          >
            {VISIBILITY_TEXT[data.visibility]}
          </Paragraph>
        )}

        <XStack flex={1} gap='$4' justifyContent='flex-end'>
          <Tooltip placement='top' delay={100}>
            <Tooltip.Trigger tag='dialog'>
              <Button
                // ref={anchorRef}
                // tag='a'
                // href={`/events/${data.prettyUrl}/settings`}
                secondary
                gap='$1'
                paddingVertical='$2'
                paddingHorizontal='$2'
                minHeight='unset'
                height={32}
                borderRadius={16}
                onPress={async () => {
                  const copyEventResult = await copyEvent({
                    variables: { eventId: data.id },
                  })

                  copyEventResult.copyEvent.event?.prettyUrl &&
                    navigate(
                      `/events/${copyEventResult.copyEvent.event?.prettyUrl}/settings`
                    )
                }}
              >
                <Icon name='save-copy-filled' color='$color' size='$4' />

                <Button.Text secondary size='$4'>
                  <Trans>Copy</Trans>
                </Button.Text>
              </Button>
            </Tooltip.Trigger>

            <Tooltip.Content>
              <Tooltip.Arrow />

              <YStack gap='$2' alignItems='center'>
                <Paragraph
                  size='$4'
                  color='$colorOnPrimary'
                  fontWeight='$5'
                  textAlign='center'
                >
                  <Trans>Copy this event into a new one</Trans>
                </Paragraph>

                <Paragraph
                  size='$4'
                  color='$colorOnPrimary'
                  fontWeight='$5'
                  textAlign='center'
                >
                  <Trans>with the same information</Trans>
                </Paragraph>
              </YStack>
            </Tooltip.Content>
          </Tooltip>

          <Button
            ref={anchorRef as any}
            tag='a'
            href={`/events/${data.prettyUrl}/settings`}
            secondary
            gap='$1'
            paddingVertical='$2'
            paddingHorizontal='$2'
            minHeight='unset'
            height={32}
            borderRadius={16}
            onPress={linkPressHandler}
            errorFlash={false}
          >
            <Icon name='settings-filled' color='$color' size='$4' />

            <Button.Text secondary size='$4'>
              <Trans>Settings</Trans>
            </Button.Text>
          </Button>
        </XStack>
      </XStack>
    )
  }

  return null
}
